import { useEffect, useMemo, useState } from 'react';
import { BboxParams } from '../../../../lib/spaceConvertor';
import { rectangleFromCoordinates } from '../../document-canvas/utils/geometry';
import { getBoxCoordinates } from '../../document-store/documentGeometry';
import {
  useCanvasGeometryState,
  useDocumentStore,
  useDocumentStoreContext,
} from '../../document-store/DocumentStore';
import { stateToInverseMatrix } from '../../document-store/helpers';
import { useDocumentContext } from '../../DocumentContext';

const LEFT_MARGIN = 24;

export const useMagicLine = ({
  pageNumber,
  position,
  sourceElementId,
}: {
  pageNumber: number;
  position: BboxParams;
  sourceElementId: string;
}) => {
  const { dimensions } = useDocumentStoreContext();
  const { canvasActionInProgress } = useDocumentContext();
  const [magicLineStartY, setMagicLineStartY] = useState<number | undefined>(
    undefined
  );

  const canvasState = useCanvasGeometryState();
  const viewportState = useDocumentStore(state => state.viewportState);

  const activeBoundingBoxCoordinates = useMemo(
    () =>
      getBoxCoordinates(
        pageNumber,
        rectangleFromCoordinates(position),
        dimensions.pages
      ),
    [dimensions.pages, pageNumber, position]
  );

  // Watch sidebar datapoint.
  useEffect(() => {
    const setStartYCoordinate = () => {
      // TODO: Use ref instead of getElementById
      const clientRect = document
        .getElementById(sourceElementId)
        // TODO: This is expensive
        ?.getBoundingClientRect();

      // For some reason, this (round + condition) is necessary to avoid rerenders.
      const y = clientRect
        ? Math.round(clientRect.y + clientRect.height / 2)
        : undefined;
      setMagicLineStartY(prev => (prev === y ? prev : y));
      frame = requestAnimationFrame(setStartYCoordinate);
    };
    let frame = requestAnimationFrame(setStartYCoordinate);

    return () => cancelAnimationFrame(frame);
  }, [sourceElementId]);

  return useMemo(() => {
    const inverseStateMatrix = stateToInverseMatrix(canvasState);
    const inverseViewportMatrix = stateToInverseMatrix(viewportState);

    const getOrigin = (y: number) =>
      DOMPoint.fromPoint({
        x: viewportState.translateX + LEFT_MARGIN,
        y,
      })
        .matrixTransform(inverseViewportMatrix)
        .matrixTransform(inverseStateMatrix);

    return !canvasActionInProgress &&
      activeBoundingBoxCoordinates &&
      magicLineStartY
      ? (() => {
          const origin = getOrigin(magicLineStartY);
          return {
            startX: origin.x,
            startY: origin.y,
            endX: activeBoundingBoxCoordinates.x,
            endY:
              activeBoundingBoxCoordinates.y +
              activeBoundingBoxCoordinates.height / 2,
          };
        })()
      : undefined;
  }, [
    activeBoundingBoxCoordinates,
    canvasActionInProgress,
    canvasState,
    magicLineStartY,
    viewportState,
  ]);
};
